import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, Radio, Select, Spin } from "antd";
import dayjs from "dayjs";
import {
  fetchPersonalHierarchy,
  fetchPersonalLink,
  saveCopyFromPersonalLink,
} from "../../service/personalStorage";
import {
  fetchBriefBankMatterFolders,
  searchBriefBankMatters,
} from "../../service/briefBank";
import { getFileIconBySize, formatFileSize } from "../../utils/fileUtils";
import { buildFolderTree } from "./personalTreeUtils";
import {
  EmptyState,
  FilterLabel,
  Header,
  MatterResult,
  MatterResults,
  Page,
  Subtitle,
  Title,
  Warning,
} from "./MyFilesStyled";

const matterLabel = (matter) =>
  matter?.display_name ||
  matter?.name ||
  matter?.client_name ||
  matter?.search_client_name ||
  `Matter ${matter?.id}`;

const normalizeList = (response, keys = []) => {
  const payload = response?.data?.data || response?.data || response || {};
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return Array.isArray(payload?.results) ? payload.results : [];
};

const apiError = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.detail || fallback;

/**
 * "You were sent a link" landing view — the substitute for Share for
 * review on a personal document (blocked server-side; see
 * documents/views/document_share.py). Any logged-in WHG Docs user who
 * opens the link can save their own independent copy, either into their
 * own My Files or into a Matter they have access to. The original owner's
 * document and folder are never touched.
 */
export default function PersonalLinkLanding() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [destination, setDestination] = useState("personal");

  const [personalHierarchy, setPersonalHierarchy] = useState(null);
  const [personalFolderId, setPersonalFolderId] = useState(null);

  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [matterFolders, setMatterFolders] = useState([]);
  const [matterFolderId, setMatterFolderId] = useState("");
  const [searchingMatters, setSearchingMatters] = useState(false);
  const [loadingMatterFolders, setLoadingMatterFolders] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    fetchPersonalLink(token)
      .then((response) => {
        if (!cancelled) setLink(response?.data);
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(
            apiError(error, "This link is invalid or has been revoked.")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (destination !== "personal" || personalHierarchy) return;
    fetchPersonalHierarchy()
      .then((response) => {
        const data = response?.data;
        setPersonalHierarchy(data);
        setPersonalFolderId(data?.root_id || null);
      })
      .catch(() => setPersonalHierarchy({ root_id: null, folders: [], documents: [] }));
  }, [destination, personalHierarchy]);

  useEffect(() => {
    if (destination !== "matter" || selectedMatter || matterQuery.trim().length < 2) {
      setMatterResults([]);
      return;
    }
    let cancelled = false;
    setSearchingMatters(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await searchBriefBankMatters(matterQuery.trim());
        if (!cancelled) setMatterResults(normalizeList(response, ["matters"]));
      } catch {
        if (!cancelled) setMatterResults([]);
      } finally {
        if (!cancelled) setSearchingMatters(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [destination, matterQuery, selectedMatter]);

  useEffect(() => {
    if (!selectedMatter?.id) {
      setMatterFolders([]);
      setMatterFolderId("");
      return;
    }
    let cancelled = false;
    setLoadingMatterFolders(true);
    fetchBriefBankMatterFolders(selectedMatter.id)
      .then((response) => {
        if (!cancelled) setMatterFolders(normalizeList(response, ["folders"]));
      })
      .catch(() => {
        if (!cancelled) setMatterFolders([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMatterFolders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMatter]);

  const personalFolderOptions = useMemo(() => {
    if (!personalHierarchy?.root_id) return [];
    const flatten = (nodes, depth = 0) =>
      nodes.flatMap((node) => [
        { value: node.record.id, label: `${"— ".repeat(depth)}${node.title}` },
        ...flatten(node.children || [], depth + 1),
      ]);
    return flatten(buildFolderTree(personalHierarchy.root_id, personalHierarchy.folders));
  }, [personalHierarchy]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload =
        destination === "personal"
          ? { destination, folder_id: personalFolderId }
          : { destination, matter_id: selectedMatter?.id, folder_id: matterFolderId || undefined };
      const response = await saveCopyFromPersonalLink(token, payload);
      setSaved(response?.data);
    } catch (error) {
      setSaveError(apiError(error, "Could not save a copy. Try again."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <Header>
          <div>
            <Title>Shared file</Title>
          </div>
        </Header>
        <Spin style={{ margin: "40px auto" }} />
      </Page>
    );
  }

  if (loadError) {
    return (
      <Page>
        <Header>
          <div>
            <Title>Shared file</Title>
          </div>
        </Header>
        <EmptyState style={{ margin: "20px 32px" }}>{loadError}</EmptyState>
      </Page>
    );
  }

  const document = link?.document;

  return (
    <Page>
      <Header>
        <div>
          <Title>{document?.name}</Title>
          <Subtitle>
            Shared by {link?.shared_by} on{" "}
            {link?.created_at ? dayjs(link.created_at).format("MMM D, YYYY") : ""} ·{" "}
            {formatFileSize(document?.size)}
          </Subtitle>
        </div>
      </Header>

      <div style={{ padding: "0 32px 32px", maxWidth: 560 }}>
        {saved ? (
          <>
            <Warning style={{ background: "#eafaf0", color: "#1a7f4b" }}>
              Saved. &quot;{saved.name}&quot; is now in your own files.
            </Warning>
            <Button type="primary" onClick={() => navigate("/my-files")}>
              Go to My Files
            </Button>
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              {getFileIconBySize(document?.name, "document", 22)}
              <span>{document?.name}</span>
            </div>

            <Warning>
              Saving creates your own independent copy — the original stays in{" "}
              {link?.shared_by}&apos;s files, untouched.
            </Warning>

            <FilterLabel style={{ marginTop: 10 }}>
              Save a copy to…
              <Radio.Group
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
                style={{ marginTop: 6 }}
              >
                <Radio value="personal">My own Files</Radio>
                <Radio value="matter">A Matter</Radio>
              </Radio.Group>
            </FilterLabel>

            {destination === "personal" && (
              <FilterLabel style={{ marginTop: 14 }}>
                Destination folder
                <Select
                  value={personalFolderId}
                  onChange={setPersonalFolderId}
                  options={personalFolderOptions}
                  loading={!personalHierarchy}
                />
              </FilterLabel>
            )}

            {destination === "matter" && (
              <>
                <FilterLabel style={{ marginTop: 14 }}>
                  Destination Matter
                  <Input
                    value={selectedMatter ? matterLabel(selectedMatter) : matterQuery}
                    placeholder="Search by client, Matter name, or number"
                    onChange={(event) => {
                      setSelectedMatter(null);
                      setMatterFolderId("");
                      setMatterQuery(event.target.value);
                    }}
                  />
                </FilterLabel>
                {searchingMatters && <Spin size="small" />}
                {matterResults.length > 0 && (
                  <MatterResults>
                    {matterResults.map((matter) => (
                      <MatterResult
                        key={matter.id}
                        type="button"
                        $selected={selectedMatter?.id === matter.id}
                        onClick={() => {
                          setSelectedMatter(matter);
                          setMatterQuery(matterLabel(matter));
                          setMatterResults([]);
                        }}
                      >
                        {matterLabel(matter)}
                      </MatterResult>
                    ))}
                  </MatterResults>
                )}
                <FilterLabel style={{ marginTop: 14 }}>
                  Destination folder
                  <Select
                    value={matterFolderId}
                    loading={loadingMatterFolders}
                    disabled={!selectedMatter}
                    onChange={setMatterFolderId}
                    options={[
                      { value: "", label: "Matter root" },
                      ...matterFolders
                        .filter((folder) => folder?.id)
                        .map((folder) => ({
                          value: folder.id,
                          label: folder.path || folder.name || `Folder ${folder.id}`,
                        })),
                    ]}
                  />
                </FilterLabel>
              </>
            )}

            {saveError && (
              <div role="alert" style={{ marginTop: 12, color: "#b42318" }}>
                {saveError}
              </div>
            )}

            <Button
              type="primary"
              style={{ marginTop: 20 }}
              loading={saving}
              disabled={
                saving ||
                (destination === "matter" && !selectedMatter?.id) ||
                (destination === "personal" && !personalFolderId)
              }
              onClick={handleSave}
            >
              Save a copy
            </Button>
          </>
        )}
      </div>
    </Page>
  );
}
